import { API_ROUTES } from '@config/routes';
import api from './api';

export type WorkflowStepType =
  | 'send_email'
  | 'send_form'
  | 'send_scheduler'
  | 'create_task'
  | 'hold_until'
  | 'start_new_flow'
  | 'manual_step';

export interface WorkflowStepTiming {
  mode: 'relative' | 'fixed';
  value?: number;
  unit?: 'minute' | 'hour' | 'day' | 'week' | 'month';
  after?: string;
  fixedDate?: string;
  fixedTime?: string;
}

export interface WorkflowStepConfig {
  subject?: string;
  body?: string;
  timing?: WorkflowStepTiming;
  requiresApproval?: boolean;
  isCompleted?: boolean;
  formId?: string;
  schedulerId?: string;
  delivery?: string;
  assigneeId?: string;
  notifyOwner?: boolean;
  flowId?: string;
  [key: string]: unknown;
}

export interface WorkflowStep {
  id: string;
  type: WorkflowStepType;
  label: string;
  description?: string;
  order: number;
  workflowKey?: string;
  workflowName?: string;
  phase?: string;
  config?: WorkflowStepConfig;
}

export interface ProjectWorkflowTemplate {
  steps: WorkflowStep[];
  isCustomized: boolean;
  templateId?: string | null;
  updatedAt?: string | null;
  removedCount?: number;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  steps: WorkflowStep[];
  isDefault?: boolean;
  description?: string;
  usedInProjects?: number;
  updatedAt?: string | null;
}

export interface TriggerProjectWorkflowStepNowResponse {
  projectId: string;
  flowId: string;
  stepId: string;
  triggeredAt: string;
  queuedForApproval?: boolean;
}

export interface ProjectWorkflowFlow {
  id: string;
  projectId: string;
  projectName?: string;
  status?: string;
  templateId?: string;
  templateName?: string;
  reused?: boolean;
}

export type WorkflowFlowStepStatus = 'pending' | 'completed' | 'skipped';
export type WorkflowFlowStatus = 'paused' | 'in_progress';

export const projectWorkflowService = {
  getTemplates: (): Promise<WorkflowTemplate[]> =>
    api.get<WorkflowTemplate[]>(API_ROUTES.WORKFLOWS.TEMPLATES),

  updateGlobalTemplate: (
    templateId: string,
    payload: { steps: WorkflowStep[]; name?: string; description?: string }
  ): Promise<WorkflowTemplate> =>
    api.put<WorkflowTemplate>(API_ROUTES.WORKFLOWS.TEMPLATE_BY_ID(templateId), payload),

  getTemplate: (projectId: string): Promise<ProjectWorkflowTemplate> =>
    api.get<ProjectWorkflowTemplate>(API_ROUTES.WORKFLOWS.PROJECT_TEMPLATE(projectId)),

  updateTemplate: (projectId: string, steps: WorkflowStep[]): Promise<ProjectWorkflowTemplate> =>
    api.put<ProjectWorkflowTemplate>(API_ROUTES.WORKFLOWS.PROJECT_TEMPLATE(projectId), {
      steps,
    }),

  resetTemplate: (projectId: string): Promise<ProjectWorkflowTemplate> =>
    api.post<ProjectWorkflowTemplate>(API_ROUTES.WORKFLOWS.PROJECT_TEMPLATE_RESET(projectId), {}),

  deleteWorkflow: (projectId: string, workflowKey: string): Promise<ProjectWorkflowTemplate> =>
    api.delete<ProjectWorkflowTemplate>(
      API_ROUTES.WORKFLOWS.PROJECT_WORKFLOW_BY_KEY(projectId, workflowKey)
    ),

  triggerStepNow: (
    projectId: string,
    stepId: string
  ): Promise<TriggerProjectWorkflowStepNowResponse> =>
    api.post<TriggerProjectWorkflowStepNowResponse>(
      API_ROUTES.WORKFLOWS.PROJECT_STEP_TRIGGER_NOW(projectId, stepId),
      {}
    ),

  startFlow: ({
    projectId,
    projectName,
    templateId,
    source = 'manual',
  }: {
    projectId: string;
    projectName?: string;
    templateId?: string | null;
    source?: string;
  }): Promise<ProjectWorkflowFlow> =>
    api.post<ProjectWorkflowFlow>(API_ROUTES.WORKFLOWS.FLOWS, {
      projectId,
      projectName,
      templateId,
      source,
    }),

  getFlows: (): Promise<ProjectWorkflowFlow[]> =>
    api.get<ProjectWorkflowFlow[]>(API_ROUTES.WORKFLOWS.FLOWS),

  updateFlowStatus: (flowId: string, status: WorkflowFlowStatus): Promise<ProjectWorkflowFlow> =>
    api.patch<ProjectWorkflowFlow>(API_ROUTES.WORKFLOWS.FLOW_STATUS(flowId), { status }),

  updateFlowStepState: (
    flowId: string,
    stepId: string,
    status: WorkflowFlowStepStatus
  ): Promise<ProjectWorkflowFlow> =>
    api.patch<ProjectWorkflowFlow>(API_ROUTES.WORKFLOWS.FLOW_STEP_STATE(flowId, stepId), {
      status,
    }),
};
